import React, { useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { GET_ALL_REGISTRATION_EMAILS } from '../Queries';
import { UserContext } from '../App';
import { Container, Paper, Button, Typography, Box, Alert } from '@mui/material';
import { Spinner } from 'reactstrap';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import { Link } from 'react-router-dom';
import MUIDataTable from "mui-datatables";

const EmailList = () => {
  const userInfo = useContext(UserContext);
  const { loading, error, data } = useQuery(GET_ALL_REGISTRATION_EMAILS, {
    fetchPolicy: "network-only"
  });

  // Check if user is admin
  const isAdmin = userInfo?.userSecLvl === "AD";

  if (!isAdmin) {
    return (
      <Container>
        <Alert severity="error">You do not have permission to access this page.</Alert>
        <br />
        <Button component={Link} to="/Home" className="yellowButton">
          <HomeIcon /> Return to Main Menu
        </Button>
      </Container>
    );
  }

  if (loading) return <Spinner color="dark" />;
  if (error) return <Alert severity="error">Error loading email list: {error.message}</Alert>;

  const emailData = data?.getAllRegistrationEmails || [];

  const handleDownloadCSV = () => {
    // Create CSV content
    const headers = ['Email', 'Case ID', 'Case Number', 'Contact Type', 'Contact Name'];
    const csvRows = [headers.join(',')];

    emailData.forEach(item => {
      const row = [
        `"${item.email || ''}"`,
        `"${item.caseID || ''}"`,
        `"${item.caseNbr || ''}"`,
        `"${item.contactType || ''}"`,
        `"${item.contactName || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rdfs_email_list_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const columns = [
    {
      name: "email",
      label: "Email",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
      name: "caseID",
      label: "Case ID",
      options: {
        filter: true,
        sort: true,
      }
    },
    {
      name: "caseNbr",
      label: "Case Number",
      options: {
        filter: true,
        sort: true,
        searchable: true,
      }
    },
    {
      name: "contactType",
      label: "Contact Type",
      options: {
        filter: true,
        sort: true,
        filterOptions: {
          names: ['Applicant', 'Owner', 'Representative']
        }
      }
    },
    {
      name: "contactName",
      label: "Contact Name",
      options: {
        filter: true,
        sort: true,
      }
    }
  ];

  const options = {
    filterType: 'dropdown',
    rowsPerPage: 25,
    rowsPerPageOptions: [10, 25, 50, 100],
    selectableRows: false,
    download: true,
    print: false,
    viewColumns: true,
    search: true,
  };

  const uniqueEmailCount = new Set(emailData.map(item => item.email).filter(Boolean)).size;

  return (
    <Container>
      <Paper style={{ padding: '20px', marginTop: '20px' }}>
        <Typography variant="h4" gutterBottom>
          RDFS Email List
        </Typography>
        <Typography variant="body1" gutterBottom>
          This list includes all email addresses from submitted RDFS registrations for:
        </Typography>
        <ul>
          <li>Applicants</li>
          <li>Owners</li>
          <li>Representatives</li>
        </ul>
        <Typography variant="body2" style={{ marginBottom: '20px', fontStyle: 'italic' }}>
          Total email entries: {emailData.length} | Unique emails: {uniqueEmailCount}
        </Typography>
        
        <Box 
          style={{ 
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="h6" style={{ marginBottom: '12px', fontWeight: 600 }}>
            Export Data
          </Typography>
          <Typography variant="body2" style={{ marginBottom: '16px', color: '#666' }}>
            Download a comprehensive CSV file containing all email addresses with case information, contact types, and contact names.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
            className="yellowButton"
            size="large"
            style={{ 
              minWidth: '200px',
              padding: '10px 24px',
              fontSize: '16px',
              fontWeight: 600,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Download CSV
          </Button>
        </Box>

        <MUIDataTable
          title="All Registration Emails"
          data={emailData}
          columns={columns}
          options={options}
        />

        <br />
        <Button
          component={Link}
          to="/Admin"
          className="yellowButton"
        >
          <HomeIcon /> Return to Admin Menu
        </Button>
      </Paper>
    </Container>
  );
};

export default EmailList;

